import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getGlobalApiConfig } from '../config/ApiConfig';
import type { ApiConfig } from '../config/ApiConfig';

class ApiService {
  private static instance: ApiService;
  private axiosInstance: AxiosInstance | null = null;
  private baseAxiosInstance: AxiosInstance | null = null;
  private currentConfigHash: string | null = null;

  private constructor() {}

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private createConfigHash(config: ApiConfig): string {
    return JSON.stringify({
      baseUrl: config.baseUrl,
      authToken: config.authToken,
      clientId: config.clientId,
    });
  }

  private createAxiosInstance(
    config: ApiConfig,
    removeApiSuffix: boolean = false
  ): AxiosInstance {
    const { baseUrl, authToken, clientId } = config;

    const finalBaseUrl = removeApiSuffix
      ? baseUrl.replace(/\/api$/, "")
      : baseUrl;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: authToken,
    };

    if (clientId) {
      headers["Clientid"] = clientId;
    }

    return axios.create({
      baseURL: finalBaseUrl,
      timeout: 30000,
      headers,
    });
  }

  private getAxiosInstance(): {
    axiosInstance: AxiosInstance;
    baseAxiosInstance: AxiosInstance;
  } {
    const apiConfig = getGlobalApiConfig();

    if (!apiConfig) {
      throw new Error(
        "API configuration not found. Make sure to wrap your component with ApiConfigProvider."
      );
    }

    const newConfigHash = this.createConfigHash(apiConfig);

    if (
      !this.axiosInstance ||
      !this.baseAxiosInstance ||
      this.currentConfigHash !== newConfigHash
    ) {
      this.axiosInstance = this.createAxiosInstance(apiConfig, false);
      this.baseAxiosInstance = this.createAxiosInstance(apiConfig, true);
      this.currentConfigHash = newConfigHash;
    }

    return {
      axiosInstance: this.axiosInstance,
      baseAxiosInstance: this.baseAxiosInstance,
    };
  }

  public axiosCruds() {
    const instance = this.getAxiosInstance().axiosInstance;
    return {
      get: <T = any>(url: string, config?: AxiosRequestConfig) =>
        instance.get<T>(url, config),
      post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        instance.post<T>(url, data, config),
      put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        instance.put<T>(url, data, config),
      patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        instance.patch<T>(url, data, config),
      delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
        instance.delete<T>(url, config),
    };
  }

  public refreshInstance(): void {
    this.axiosInstance = null;
    this.baseAxiosInstance = null;
    this.currentConfigHash = null;
  }

  public baseAxiosCruds() {
    const instance = this.getAxiosInstance().baseAxiosInstance;
    return {
      get: <T = any>(url: string, config?: AxiosRequestConfig) =>
        instance.get<T>(url, config),
      post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        instance.post<T>(url, data, config),
      put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        instance.put<T>(url, data, config),
      patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        instance.patch<T>(url, data, config),
      delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
        instance.delete<T>(url, config),
    };
  }
}

export const apiService = ApiService.getInstance();
export { ApiService };
